import styled, { keyframes } from 'styled-components'

import AuthLayout from '../pages/AuthLayout'

const FORM_VARIANTS = {
  email: {
    label: '이메일 인증 화면을 준비하는 중',
    stepWidth: '150px',
    titleWidths: ['72%'],
    descriptionWidth: '88%',
    fields: 1,
    showAgreements: false,
    showSummary: false,
  },
  'sign-up': {
    label: '회원가입 화면을 준비하는 중',
    stepWidth: '104px',
    titleWidths: ['92%', '44%'],
    descriptionWidth: '96%',
    fields: 3,
    showAgreements: false,
    showSummary: true,
  },
  'sign-up-consent': {
    label: '회원가입 약관 동의 화면을 준비하는 중',
    stepWidth: '104px',
    titleWidths: ['92%', '58%'],
    descriptionWidth: '92%',
    fields: 0,
    showAgreements: true,
    showSummary: false,
  },
  consent: {
    label: '약관 및 정책 안내 화면을 준비하는 중',
    stepWidth: '112px',
    titleWidths: ['94%', '58%'],
    descriptionWidth: '92%',
    fields: 0,
    showAgreements: true,
    showSummary: false,
  },
  'password-reset': {
    label: '비밀번호 재설정 화면을 준비하는 중',
    stepWidth: '126px',
    titleWidths: ['78%'],
    descriptionWidth: '90%',
    fields: 2,
    showAgreements: false,
    showSummary: false,
  },
}

export default function AuthRouteFallback({ variant = 'email' }) {
  const config = FORM_VARIANTS[variant] || FORM_VARIANTS.email

  return (
    <AuthLayout>
      <SkeletonRoot
        role="status"
        aria-live="polite"
        aria-label={config.label}
        aria-busy="true"
      >
        <HeaderSkeleton>
          <SkeletonBlock $height="14px" $width={config.stepWidth} />
          <TitleGroup>
            {config.titleWidths.map((width) => (
              <SkeletonBlock key={width} $height="38px" $width={width} />
            ))}
          </TitleGroup>
          <SkeletonBlock $height="16px" $width={config.descriptionWidth} />
        </HeaderSkeleton>

        <FormSkeleton>
          {config.showSummary && <SkeletonBlock $height="66px" $radius="12px" />}
          {Array.from({ length: config.fields }).map((_, index) => (
            <FieldSkeleton key={index}>
              <SkeletonBlock $height="13px" $width={index === 0 ? '72px' : '88px'} />
              <SkeletonBlock $height="50px" $radius="12px" />
              {variant === 'sign-up' && index < 2 && (
                <SkeletonBlock $height="12px" $width={index === 0 ? '68%' : '58%'} />
              )}
            </FieldSkeleton>
          ))}
          {config.showAgreements && (
            <AgreementSkeleton>
              <SkeletonBlock $height="42px" $radius="10px" />
              {Array.from({ length: 5 }).map((_, index) => (
                <SkeletonBlock
                  key={index}
                  $height={index < 3 ? '54px' : '44px'}
                  $radius="6px"
                />
              ))}
            </AgreementSkeleton>
          )}
          <SkeletonBlock $height="50px" $radius="12px" />
        </FormSkeleton>

        <FooterSkeleton>
          <SkeletonBlock $height="13px" $width="112px" />
        </FooterSkeleton>
      </SkeletonRoot>
    </AuthLayout>
  )
}

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`

const SkeletonRoot = styled.div`
  width: 100%;
`

const HeaderSkeleton = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 34px;
`

const TitleGroup = styled.div`
  display: grid;
  gap: 8px;
  margin: 2px 0;
`

const FormSkeleton = styled.div`
  display: grid;
  gap: 20px;
`

const FieldSkeleton = styled.div`
  display: grid;
  gap: 8px;
`

const AgreementSkeleton = styled.div`
  display: grid;
  gap: 8px;
  padding-top: 22px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const FooterSkeleton = styled.div`
  display: grid;
  margin-top: 28px;
  place-items: center;
`

const SkeletonBlock = styled.div`
  width: ${({ $width = '100%' }) => $width};
  height: ${({ $height }) => $height};
  border-radius: ${({ $radius = '6px' }) => $radius};
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(90deg, #222B39 0%, #303B4E 48%, #222B39 100%)'
    : 'linear-gradient(90deg, #EEF0F4 0%, #F8F9FB 48%, #EEF0F4 100%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`
